import React, { useState, useEffect } from "react";
import "../../styles/common/trueFalseViewer.scss";
import { parseJsonRobustly } from "../../helper/jsonUtils";
import {
  updatePageForCourse,
  updatePageForSubject,
  updatePageForTopic,
  updatePageForSubtopic,
  getPageForCourse,
  getPageForSubject,
  getPageForTopic,
  getPageForSubtopic,
} from "../../helper/apicalls/course";
import { AddIcon, EditIcon, DeleteIcon } from "../../icons";
import DeleteConfirmPopUp from "./deleteConfirmPopUp";
import { updateInitialTest } from "../../helper/apicalls/initialTest";
import GlobalLoader from "./GlobalLoader";

const TrueFalseViewer = ({
  questionData,
  onUpdate,
  pageId,
  pageData,
  context,
  courseKey,
  subjectKey,
  topicKey,
  subTopicKey,
  contentIndex,
  onAddNext,
  title = "True / False",
  startInEditMode = false,
  externalSaveSignal,
  externalCancelSignal,
  allowEditWithoutPersist = false,
  permission,
  provideUpdatedBuilder,
}) => {
  const [questions, setQuestions] = useState([]);
  const [editMode, setEditMode] = useState(!!startInEditMode);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setQuestions([]);
    if (!questionData) return;

    try {
      let parsedData = questionData;
      if (typeof questionData === "string") {
        parsedData = parseJsonRobustly(questionData);
      }
      if (
        parsedData &&
        typeof parsedData === "object" &&
        parsedData.structured
      ) {
        parsedData = parsedData.structured;
      }

      if (Array.isArray(parsedData)) {
        setQuestions(parsedData);
      } else if (
        parsedData &&
        typeof parsedData === "object" &&
        parsedData.questions
      ) {
        setQuestions(parsedData.questions);
      } else if (parsedData && typeof parsedData === "object") {
        setQuestions([parsedData]);
      }
    } catch (error) {
      console.error("Error parsing True/False data:", error);
      setQuestions([
        {
          question:
            typeof questionData === "string"
              ? questionData
              : JSON.stringify(questionData, null, 2),
          options: [{ A: "True" }, { B: "False" }],
          answer: "A",
        },
      ]);
    }
  }, [questionData]);

  useEffect(() => {
    if (!externalSaveSignal) return;
    if (typeof onUpdate !== "function") return;
    try {
      const tempOriginal = { type: "trueorfalse", content: "{}" };
      const updatedItem = buildUpdatedItemFromOriginal(tempOriginal);
      onUpdate({ content: [updatedItem] });
    } catch (e) {
      console.error("Error updating True/False data:", e);
    }
  }, [externalSaveSignal]);

  useEffect(() => {
    if (!externalCancelSignal) return;
    setQuestions((prev) => prev);
    setEditMode(!!startInEditMode);
  }, [externalCancelSignal]);

  const normalizeAnswer = (answer) => {
    if (typeof answer === "boolean") {
      return answer ? "true" : "false";
    }
    if (typeof answer === "string") {
      const lowerAnswer = answer.toLowerCase();
      if (
        lowerAnswer === "true" ||
        lowerAnswer === "1" ||
        lowerAnswer === "a" ||
        lowerAnswer === "yes"
      ) {
        return "true";
      }
      if (
        lowerAnswer === "false" ||
        lowerAnswer === "0" ||
        lowerAnswer === "b" ||
        lowerAnswer === "no"
      ) {
        return "false";
      }
    }
    return answer;
  };

  const buildUpdatedItemFromOriginal = (originalItem) => {
    const cloned = JSON.parse(JSON.stringify(originalItem ?? {}));
    const out = cloned && typeof cloned === "object" ? cloned : {};

    let originalQuestions = [];

    if (out && typeof out.content === "string") {
      try {
        const parsed = JSON.parse(out.content);
        if (parsed && typeof parsed === "object") {
          out.content = parsed;
        }
      } catch (_) {
        out.content = {};
      }
    }

    if (
      out &&
      out.content &&
      typeof out.content === "object" &&
      !Array.isArray(out.content) &&
      Array.isArray(out.content.questions)
    ) {
      originalQuestions = out.content.questions;
    } else if (Array.isArray(out?.questions)) {
      originalQuestions = out.questions;
      if (!out.content || typeof out.content !== "object") out.content = {};
      out.content.questions = originalQuestions;
    } else {
      out.type = out.type || "trueorfalse";
      if (!out.content || typeof out.content !== "object") out.content = {};
      out.content.questions = [];
      originalQuestions = out.content.questions;
    }

    let updated;
    if (originalQuestions.length === 0 && questions.length > 0) {
      updated = questions.map((q) => ({
        ...q,
        question: q.question || "",
        answer: q.answer || "true",
        options: q.options || [{ A: "True" }, { B: "False" }],
      }));
    } else {
      updated = (originalQuestions || []).map((oq, i) => {
        const newQ = JSON.parse(JSON.stringify(oq));
        const eq = questions[i] || {};

        const originalQText = oq.question || oq.title || oq.text || "";
        if ((eq.question || "") !== originalQText) {
          if ("question" in newQ) newQ.question = eq.question || "";
          else if ("title" in newQ) newQ.title = eq.question || "";
          else if ("text" in newQ) newQ.text = eq.question || "";
          else newQ.question = eq.question || "";
        }

        const eqAns = normalizeAnswer(
          eq.answer || eq.correct_answer || eq.correct || "",
        );
        if (eqAns) {
          if ("answer" in newQ) newQ.answer = eqAns;
          else if ("correct" in newQ) newQ.correct = eqAns;
          else if ("correct_answer" in newQ) newQ.correct_answer = eqAns;
          else newQ.answer = eqAns;
        }

        if (typeof eq.explanation === "string") {
          if ("explanation" in newQ) newQ.explanation = eq.explanation;
          else newQ.explanation = eq.explanation;
        }

        return newQ;
      });
    }

    out.type = out.type || "trueorfalse";
    out.content = out.content || {};
    out.content.questions = updated;
    out.content = JSON.stringify(out.content);
    return out;
  };

  useEffect(() => {
    if (typeof provideUpdatedBuilder === "function") {
      provideUpdatedBuilder((originalItem) =>
        buildUpdatedItemFromOriginal(originalItem),
      );
    }
  }, [provideUpdatedBuilder, questions]);

  const doUpdate = async () => {
    if (!pageId || !context) return;
    let contentArray = [];
    try {
      let rawContent = pageData?.content;
      if (typeof rawContent === "string") {
        const parsed = JSON.parse(rawContent);
        if (Array.isArray(parsed)) contentArray = parsed;
        else if (parsed && typeof parsed === "object") contentArray = [parsed];
      } else if (Array.isArray(rawContent)) {
        contentArray = rawContent;
      } else if (rawContent && typeof rawContent === "object") {
        contentArray = [rawContent];
      }
    } catch (e) {
      return;
    }

    if (!Array.isArray(contentArray)) return;
    if (
      typeof contentIndex !== "number" ||
      contentIndex < 0 ||
      contentIndex >= contentArray.length
    )
      return;

    const updatedArray = [...contentArray];
    const originalItem = updatedArray[contentIndex];
    const updatedItem = buildUpdatedItemFromOriginal(originalItem);
    updatedArray[contentIndex] = updatedItem;

    const payload = { id: pageId, content: updatedArray };
    let updated;

    setLoading(true);
    try {
      switch (context) {
        case "course":
          await updatePageForCourse(payload);
          updated = await getPageForCourse(courseKey, pageId);
          break;
        case "subject":
          await updatePageForSubject(payload);
          updated = await getPageForSubject(subjectKey, pageId);
          break;
        case "topic":
          await updatePageForTopic(payload);
          updated = await getPageForTopic(topicKey, pageId);
          break;
        case "subtopic":
          await updatePageForSubtopic(payload);
          updated = await getPageForSubtopic(subTopicKey, pageId);
          break;
        case "initial-test": {
          const testPayload = { content: updatedArray };
          const resp = await updateInitialTest(pageId, testPayload);
          if (resp && Array.isArray(resp.content)) {
            updated = resp;
          } else {
            updated = {
              ...(pageData || {}),
              id: pageId,
              content: updatedArray,
            };
          }
          break;
        }
        default:
          updated = payload;
      }
      if (onUpdate) onUpdate(updated);
      setEditMode(false);
    } finally {
      setLoading(false);
    }
  };

  const doDelete = async () => {
    if (!pageId || !context) return;
    let contentArray = [];
    try {
      let rawContent = pageData?.content;
      if (typeof rawContent === "string") {
        const parsed = JSON.parse(rawContent);
        if (Array.isArray(parsed)) contentArray = parsed;
        else if (parsed && typeof parsed === "object") contentArray = [parsed];
      } else if (Array.isArray(rawContent)) {
        contentArray = rawContent;
      } else if (rawContent && typeof rawContent === "object") {
        contentArray = [rawContent];
      }
    } catch (e) {
      return;
    }

    if (!Array.isArray(contentArray)) return;
    if (
      typeof contentIndex !== "number" ||
      contentIndex < 0 ||
      contentIndex >= contentArray.length
    )
      return;

    const updatedArray = contentArray.filter((_, idx) => idx !== contentIndex);

    const payload = { id: pageId, content: updatedArray };
    let updated;

    setLoading(true);
    try {
      switch (context) {
        case "course":
          await updatePageForCourse(payload);
          updated = await getPageForCourse(courseKey, pageId);
          break;
        case "subject":
          await updatePageForSubject(payload);
          updated = await getPageForSubject(subjectKey, pageId);
          break;
        case "topic":
          await updatePageForTopic(payload);
          updated = await getPageForTopic(topicKey, pageId);
          break;
        case "subtopic":
          await updatePageForSubtopic(payload);
          updated = await getPageForSubtopic(subTopicKey, pageId);
          break;
        case "initial-test": {
          const testPayload = { content: updatedArray };
          const resp = await updateInitialTest(pageId, testPayload);
          if (resp && Array.isArray(resp.content)) {
            updated = resp;
          } else {
            updated = {
              ...(pageData || {}),
              id: pageId,
              content: updatedArray,
            };
          }
          break;
        }
        default:
          updated = payload;
      }

      if (onUpdate) onUpdate(updated);
      setIsDeleteConfirmOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const Button = ({
    children,
    onClick,
    variant,
    size,
    className,
    disabled,
  }) => {
    const baseClass = "tf-button";
    const variantClass = variant ? `tf-button--${variant}` : "";
    const sizeClass = size ? `tf-button--${size}` : "";
    const disabledClass = disabled ? "tf-button--disabled" : "";
    const classes = `${baseClass} ${variantClass} ${sizeClass} ${disabledClass} ${
      className || ""
    }`;

    return (
      <button className={classes} onClick={onClick} disabled={disabled}>
        {children}
      </button>
    );
  };

  const Radio = ({ checked, onChange, className }) => {
    return (
      <input
        type="radio"
        className={`tf-radio ${className || ""}`}
        checked={checked}
        onChange={onChange}
      />
    );
  };

  return (
    <div
      className={`true-false-viewer true-false-viewer--${
        editMode ? "edit" : "view"
      }`}
    >
      {loading && <GlobalLoader visible={loading} text="Processing..." />}
      {isDeleteConfirmOpen && (
        <DeleteConfirmPopUp
          isConfirmDeleteOpen={isDeleteConfirmOpen}
          setIsConfirmDeleteOpen={setIsDeleteConfirmOpen}
          handleDelete={doDelete}
          title="Delete question"
          message="Are you sure you want to delete this True/False item? This action cannot be undone."
          confirmLabel="Delete"
          cancelLabel="Cancel"
        />
      )}
      <div className="true-false-viewer__header">
        <h3>{title}</h3>
        {questions.length > 0 &&
          (questions[0].categories || questions[0].category) && (
            <span className="question-category-chip">
              {String(questions[0].categories || questions[0].category)
                .replace(/_/g, " ")
                .replace(/\b\w/g, (l) => l.toUpperCase())}
            </span>
          )}
      </div>
      {questions.map((q, index) => {
        const question = q.question || q.title || q.text || "No question text";
        const correctAnswer = normalizeAnswer(
          q.answer || q.correct_answer || q.correct || "true",
        );
        const normalizedCorrectAnswer = normalizeAnswer(correctAnswer);

        return (
          <div
            key={index}
            className={`tf-question tf-question--${editMode ? "edit" : "view"}`}
          >
            {editMode ? (
              <div className="tf-question__edit">
                <div
                  className={`tf-textfield tf-textfield--fullwidth tf-question__input tf-question__input--edit`}
                >
                  <label className="tf-textfield__label">{`Question ${index + 1}`}</label>
                  <input
                    className="tf-textfield__input"
                    value={question}
                    onChange={(e) => {
                      const val = e.target.value;
                      setQuestions((prev) =>
                        prev.map((qq, i) =>
                          i === index ? { ...qq, question: val } : qq,
                        ),
                      );
                    }}
                  />
                </div>
                <div className="tf-answer tf-answer--edit">
                  <div
                    className={`tf-option ${
                      normalizedCorrectAnswer === "true"
                        ? "tf-option--correct"
                        : ""
                    }`}
                    onClick={() => {
                      setQuestions((prev) =>
                        prev.map((qq, i) =>
                          i === index ? { ...qq, answer: "true" } : qq,
                        ),
                      );
                    }}
                  >
                    <Radio
                      checked={normalizedCorrectAnswer === "true"}
                      onChange={() => {
                        setQuestions((prev) =>
                          prev.map((qq, i) =>
                            i === index ? { ...qq, answer: "true" } : qq,
                          ),
                        );
                      }}
                      className="tf-option__radio"
                    />
                    <span>True</span>
                  </div>
                  <div
                    className={`tf-option ${
                      normalizedCorrectAnswer === "false"
                        ? "tf-option--correct"
                        : ""
                    }`}
                    onClick={() => {
                      setQuestions((prev) =>
                        prev.map((qq, i) =>
                          i === index ? { ...qq, answer: "false" } : qq,
                        ),
                      );
                    }}
                  >
                    <Radio
                      checked={normalizedCorrectAnswer === "false"}
                      onChange={() => {
                        setQuestions((prev) =>
                          prev.map((qq, i) =>
                            i === index ? { ...qq, answer: "false" } : qq,
                          ),
                        );
                      }}
                      className="tf-option__radio"
                    />
                    <span>False</span>
                  </div>
                </div>
                <div
                  className={`tf-textfield tf-textfield--fullwidth tf-question__explanation tf-question__explanation--edit`}
                >
                  <label className="tf-textfield__label">Explanation</label>
                  <textarea
                    className="tf-textfield__input"
                    rows={2}
                    value={q.explanation || ""}
                    onChange={(e) => {
                      const val = e.target.value;
                      setQuestions((prev) =>
                        prev.map((qq, i) =>
                          i === index ? { ...qq, explanation: val } : qq,
                        ),
                      );
                    }}
                  />
                </div>
              </div>
            ) : (
              <>
                <div
                  className={`tf-textfield tf-textfield--fullwidth tf-question__input tf-question__input--view tf-textfield--readonly`}
                >
                  <label className="tf-textfield__label">{`Question ${index + 1}`}</label>
                  <input
                    className="tf-textfield__input"
                    value={question}
                    readOnly={true}
                    onChange={(e) => {
                      const val = e.target.value;
                      setQuestions((prev) =>
                        prev.map((qq, i) =>
                          i === index ? { ...qq, question: val } : qq,
                        ),
                      );
                    }}
                  />
                </div>
                <div className="tf-answer">
                  <div
                    className={`tf-option ${
                      normalizedCorrectAnswer === "true"
                        ? "tf-option--correct"
                        : ""
                    }`}
                  >
                    <span className="tf-option__label">A.</span>
                    <span className="tf-option__text">True</span>
                    {normalizedCorrectAnswer === "true" && (
                      <span className="tf-option__indicator">✓</span>
                    )}
                  </div>
                  <div
                    className={`tf-option ${
                      normalizedCorrectAnswer === "false"
                        ? "tf-option--correct"
                        : ""
                    }`}
                  >
                    <span className="tf-option__label">B.</span>
                    <span className="tf-option__text">False</span>
                    {normalizedCorrectAnswer === "false" && (
                      <span className="tf-option__indicator">✓</span>
                    )}
                  </div>
                </div>
                {q.explanation && !editMode && (
                  <div className="tf-explanation">
                    <strong>Explanation:</strong> {q.explanation}
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}
      {permission && (
        <div className="true-false-viewer__actions">
          {(pageId || allowEditWithoutPersist) &&
            (editMode ? (
              <>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => setEditMode(false)}
                  className="true-false-viewer__action-button"
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={
                    pageId
                      ? doUpdate
                      : () => {
                          if (onUpdate) {
                            const tempOriginal = {
                              type: "trueorfalse",
                              content: "{}",
                            };
                            const updatedItem =
                              buildUpdatedItemFromOriginal(tempOriginal);
                            onUpdate({ content: [updatedItem] });
                          }
                          setEditMode(false);
                        }
                  }
                  className="true-false-viewer__action-button"
                >
                  Save
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => setEditMode(true)}
                  className="true-false-viewer__action-button"
                >
                  <EditIcon className="toolbar-button-icon" />
                  Edit
                </Button>
                {(pageId || allowEditWithoutPersist) && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => {
                      if (pageId) setIsDeleteConfirmOpen(true);
                      else if (onUpdate) onUpdate({ content: [] });
                    }}
                    className="true-false-viewer__action-button"
                  >
                    <DeleteIcon className="toolbar-button-icon" />
                    Delete
                  </Button>
                )}
                {onAddNext && pageId && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={onAddNext}
                    className="true-false-viewer__action-button"
                  >
                    <AddIcon className="toolbar-button-icon" />
                    Add Next
                  </Button>
                )}
              </>
            ))}
        </div>
      )}
    </div>
  );
};

export default TrueFalseViewer;
